import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Search,
  Package,
  Hammer,
  Loader2,
  Calculator,
  AlertCircle,
  AlertTriangle,
  CalendarClock,
  X,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { Modal } from '@components/Dialog';
import { DateInputBR } from '@components/DateInputBR';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { salesOrderService } from '../../services/salesOrderService';
import { rawMaterialService } from '../../services/rawMaterialService';
import { providerService } from '../../services/providerService';
import { companyService } from '../../services/companyService';
import { Company, formatCnpj } from '../../types/company';
import { financialService } from '@services/financialService';
import { CategorySelect } from '@pages/financial/financialShared';
import { buildCategoryOptions } from '@pages/financial/financialUtils';
import { ApiError } from '../../utils/apiError';
import {
  detailsToFormErrors,
  installmentErrorKey,
  installmentsTotalMismatch,
  validateInstallments,
  withoutInstallmentErrors,
} from './purchaseOrderFormUtils';
import {
  buildInstallmentsFromTerm,
  buildMonthlyInstallments,
  cardInstallmentErrors,
  defaultCardId,
  describeTerm,
  dueBaseLabel,
  isCreditCardMethodName,
  recalcDueDates,
  type DueDateChange,
} from './paymentTermUtils';
import { DueDatesUpdatedModal, SaveWarningsModal } from './PurchaseOrderInstallmentModals';
import type { ProviderPaymentTerm } from '../../types/providerPaymentTerm';
import type { CategoryOptionGroup } from '@pages/financial/financialUtils';
import {
  PurchaseOrderFormData,
  PurchaseOrderItemRow,
  PurchaseOrderInstallmentRow,
  PurchaseOrderSalesOrderLink,
  PaymentMethod,
  PaymentMethodModel,
  PurchaseCreditCard,
  PURCHASE_ORDER_STATUS_OPTIONS,
  FREIGHT_RESPONSIBILITY_OPTIONS,
  DISCOUNT_UNIT_OPTIONS,
  ProviderProduct,
  PurchaseOrderPrefill,
} from '../../types/purchaseOrder';
import { RawMaterial } from '../../types/rawMaterial';
import { Provider } from '../../types/provider';
import { UNIT_OPTIONS } from '../../types/rawMaterial';

// ─── Helpers ─────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 10);

/** Hoje no calendário local, "AAAA-MM-DD" (sem toISOString, que é UTC). */
const localTodayISO = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'R$ 0,00';

// ─── Search Modal (generic) ─────────────────────────────────────

interface SearchModalProps<T> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSelect: (item: T) => void;
  searchFn: (q: string) => Promise<T[]>;
  renderItem: (item: T) => React.ReactNode;
  keyFn: (item: T) => string | number;
}

function SearchModal<T>({ isOpen, onClose, title, onSelect, searchFn, renderItem, keyFn }: SearchModalProps<T>) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) { setQuery(''); setResults([]); return; }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await searchFn(query);
        setResults(items);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">{title}</h3>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            autoFocus
            className="input pl-10 w-full"
            placeholder="Digite para buscar..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-emerald-600" size={24} />
            </div>
          )}
          {!loading && results.length === 0 && query.trim() && (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhum resultado encontrado.</div>
          )}
          {!loading && results.length === 0 && !query.trim() && (
            <div className="text-center py-8 text-slate-400 text-sm">Digite para pesquisar.</div>
          )}
          {!loading && results.map(item => (
            <button
              key={keyFn(item)}
              className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
              onClick={() => { onSelect(item); onClose(); }}
            >
              {renderItem(item)}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Component ──────────────────────────────────────────────

export const PurchaseOrderFormView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  // Pré-preenchimento vindo do Planejamento de Compras ("Pedido para X meses", RN-EST-75).
  const prefill = !isEditing ? (location.state as { prefill?: PurchaseOrderPrefill } | null)?.prefill : undefined;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Lookups
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentMethodModels, setPaymentMethodModels] = useState<PaymentMethodModel[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Provider label
  const [providerLabel, setProviderLabel] = useState('');

  // Modal states
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [rawMaterialModalOpen, setRawMaterialModalOpen] = useState(false);

  // Installment generation
  // Categorias financeiras da empresa (para a categoria das provisões).
  const [categoryGroups, setCategoryGroups] = useState<CategoryOptionGroup[]>([]);
  const [installmentCount, setInstallmentCount] = useState<number | ''>('');

  // Condição de pagamento do fornecedor (gera as parcelas) e cartões da empresa
  // (parcela paga com cartão provisiona na fatura do cartão escolhido).
  const [providerTerm, setProviderTerm] = useState<ProviderPaymentTerm | null>(null);
  const [creditCards, setCreditCards] = useState<PurchaseCreditCard[]>([]);
  const [installmentNotice, setInstallmentNotice] = useState<string | null>(null);
  const [dueDateChanges, setDueDateChanges] = useState<{ changes: DueDateChange[]; fieldLabel: string } | null>(null);
  const [saveWarnings, setSaveWarnings] = useState<string[] | null>(null);

  // Form
  const [formData, setFormData] = useState<PurchaseOrderFormData>({
    order_date: localTodayISO(),
    status: 0,
    is_priority: false,
    is_active: true,
    items: [],
    installments: [],
    sales_order_ids: [],
  });

  // ── Vínculo com Pedidos de Venda (N:N) ────────────────────────
  // linkedSalesOrders guarda o rótulo (cliente etc.) dos PVs selecionados;
  // o payload envia só os ids (sales_order_ids, replace-all no backend).
  const [linkedSalesOrders, setLinkedSalesOrders] = useState<PurchaseOrderSalesOrderLink[]>([]);
  const [salesOrderQuery, setSalesOrderQuery] = useState('');
  const [salesOrderResults, setSalesOrderResults] = useState<PurchaseOrderSalesOrderLink[]>([]);
  const [salesOrderSearching, setSalesOrderSearching] = useState(false);

  const searchSalesOrders = async () => {
    const query = salesOrderQuery.trim();
    if (!query) return;
    setSalesOrderSearching(true);
    try {
      const digits = query.replace(/\D/g, '');
      const isNumeric = digits.length > 0 && digits.length === query.replace(/[#\s]/g, '').length;
      const result = await salesOrderService.getOrders({
        page: 1,
        pageSize: 10,
        ...(isNumeric ? { searchName: digits } : { customer_name: query }),
      });
      setSalesOrderResults(
        result.data.map(order => ({
          order_id: order.order_id,
          customer_name: order.customer_name ?? undefined,
          order_status: order.order_status ?? undefined,
          is_priority: order.is_priority === true,
        })),
      );
    } catch {
      setSalesOrderResults([]);
    } finally {
      setSalesOrderSearching(false);
    }
  };

  const addSalesOrderLink = (link: PurchaseOrderSalesOrderLink) => {
    if ((formData.sales_order_ids ?? []).includes(link.order_id)) return;
    setLinkedSalesOrders(prev => [...prev, link]);
    setFormData(prev => ({ ...prev, sales_order_ids: [...(prev.sales_order_ids ?? []), link.order_id] }));
  };

  const removeSalesOrderLink = (orderId: number) => {
    setLinkedSalesOrders(prev => prev.filter(l => l.order_id !== orderId));
    setFormData(prev => ({ ...prev, sales_order_ids: (prev.sales_order_ids ?? []).filter(oid => oid !== orderId) }));
  };

  // ── Load lookups ──────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const [methods, models, companiesList] = await Promise.all([
          purchaseOrderService.getPaymentMethods(),
          purchaseOrderService.getPaymentMethodModels(),
          companyService.getCompanies(),
        ]);
        setPaymentMethods(methods);
        setPaymentMethodModels(models);
        setCompanies(companiesList);
      } catch { /* ignore */ }
    })();
  }, []);

  // ── Load existing order ───────────────────────────────────────

  useEffect(() => {
    if (!isEditing || !id) return;
    (async () => {
      try {
        setLoading(true);
        const order = await purchaseOrderService.getPurchaseOrderById(parseInt(id));
        setFormData({
          order_number: order.order_number,
          order_date: order.order_date || '',
          expected_date: order.expected_date || '',
          company_id: order.company_id,
          provider_id: order.provider_id,
          status: order.status,
          purchase_order_reference: order.purchase_order_reference || '',
          notes: order.notes || '',
          internal_notes: order.internal_notes || '',
          discount_value: order.discount_value,
          discount_unit: order.discount_unit || 'REAL',
          total_icms: order.total_icms,
          total_ipi: order.total_ipi,
          freight_cost: order.freight_cost,
          carrier: order.carrier || '',
          freight_responsibility: order.freight_responsibility,
          gross_weight: order.gross_weight,
          volumes: order.volumes,
          payment_method_model_id: order.payment_method_model_id,
          accounting_plan_id: order.accounting_plan_id,
          is_priority: order.is_priority === true,
          is_active: order.is_active,
          sales_order_ids: (order.sales_orders ?? []).map(l => l.order_id),
          items: (order.items || []).map(item => ({
            ...item,
            _tempId: uid(),
            _type: item.raw_material_id ? 'raw_material' as const : 'product' as const,
            _sourceCode: item.sku || '',
          })),
          installments: (order.installments || []).map(inst => ({
            ...inst,
            _tempId: uid(),
          })),
        });
        setProviderLabel(order.provider_name || '');
        setLinkedSalesOrders(order.sales_orders ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar pedido');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEditing]);

  // ── Prefill (novo PC a partir do planejamento) ────────────────

  useEffect(() => {
    if (!prefill) return;
    setFormData(prev => ({
      ...prev,
      provider_id: prefill.provider_id,
      internal_notes: prefill.internal_notes ?? prev.internal_notes,
      items: prefill.items.map(it => ({
        _tempId: uid(),
        _type: 'product' as const,
        _sourceCode: it.sku,
        description: it.description,
        unit: it.unit || 'UN',
        unit_value: it.unit_value,
        quantity: it.quantity,
        product_id: it.product_id,
        raw_material_id: undefined,
      })),
    }));
    setProviderLabel(prefill.provider_name);
  }, [prefill]);

  // ── Field helpers ─────────────────────────────────────────────

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  // ── Provider search ───────────────────────────────────────────

  const searchProviders = useCallback(async (q: string): Promise<Provider[]> => {
    try {
      const res = await providerService.getProviders({
        page: 1, pageSize: 20, searchName: q,
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch { return []; }
  }, []);

  const handleProviderSelect = (provider: Provider) => {
    const previousProviderId = formData.provider_id;
    const hasProductItems = formData.items.some(i => i._type === 'product');

    updateField('provider_id', provider.provider_id);
    setProviderLabel(provider.trade_name || provider.legal_name);

    // Se mudou de fornecedor e havia itens de produto, limpa os itens de produto
    if (previousProviderId && previousProviderId !== provider.provider_id && hasProductItems) {
      setFormData(prev => ({
        ...prev,
        provider_id: provider.provider_id,
        items: prev.items.filter(i => i._type !== 'product'),
      }));
    }
  };

  // ── Product search (by provider) ──────────────────────────────

  const searchProducts = useCallback(async (q: string): Promise<ProviderProduct[]> => {
    if (!formData.provider_id) return [];
    try {
      return await purchaseOrderService.searchProductsByProvider(formData.provider_id, q);
    } catch { return []; }
  }, [formData.provider_id]);

  const handleProductSelect = (product: ProviderProduct) => {
    const newItem: PurchaseOrderItemRow = {
      _tempId: uid(),
      _type: 'product',
      _sourceCode: product.sku,
      description: product.description,
      unit: product.unit || 'UN',
      unit_value: product.provider_cost,
      quantity: 1,
      product_id: product.product_id,
      raw_material_id: undefined,
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  /** Abre modal de produto somente se fornecedor estiver selecionado */
  const openProductModal = () => {
    if (!formData.provider_id) {
      setErrors(prev => ({ ...prev, provider_id: 'Selecione um fornecedor antes de adicionar produtos' }));
      return;
    }
    setProductModalOpen(true);
  };

  // ── Raw material search ───────────────────────────────────────

  const searchRawMaterials = useCallback(async (q: string): Promise<RawMaterial[]> => {
    try {
      const res = await rawMaterialService.searchRawMaterials(q, 1, 20, { is_active: true });
      return res.rawMaterials || [];
    } catch { return []; }
  }, []);

  const handleRawMaterialSelect = (rm: RawMaterial) => {
    const newItem: PurchaseOrderItemRow = {
      _tempId: uid(),
      _type: 'raw_material',
      _sourceCode: rm.code,
      description: rm.description,
      unit: rm.unit || 'UN',
      unit_value: rm.cost || 0,
      quantity: 1,
      product_id: undefined,
      raw_material_id: rm.id,
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  // ── Items CRUD ────────────────────────────────────────────────

  const updateItem = (tempId: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(i => i._tempId === tempId ? { ...i, [field]: value } : i),
    }));
  };

  const removeItem = (tempId: string) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter(i => i._tempId !== tempId) }));
  };

  // ── Installments CRUD ─────────────────────────────────────────

  const addInstallment = () => {
    const inst: PurchaseOrderInstallmentRow = {
      _tempId: uid(),
      value: 0,
      due_date: '',
      notes: '',
      payment_method_id: providerTerm?.payment_method_id ?? paymentMethods[0]?.payment_method_id,
    };
    if (isCardMethod(inst.payment_method_id)) inst.credit_card_id = defaultCardId(creditCards);
    setFormData(prev => ({ ...prev, installments: [...prev.installments, inst] }));
  };

  const updateInstallment = (tempId: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      installments: prev.installments.map(i => {
        if (i._tempId !== tempId) return i;
        const next: PurchaseOrderInstallmentRow = { ...i, [field]: value };
        // Vencimento digitado à mão vira data fixa: não é mais recalculado.
        if (field === 'due_date') {
          next.due_date_base = null;
          next.due_days = null;
        }
        // Cartão só existe em forma de pagamento de cartão de crédito.
        if (field === 'payment_method_id') {
          next.credit_card_id = isCardMethod(value) ? (i.credit_card_id ?? defaultCardId(creditCards)) : null;
        }
        return next;
      }),
    }));
    // Mexeu numa parcela: os erros de parcela saem e voltam (revalidados) ao salvar.
    setErrors(prev => withoutInstallmentErrors(prev));
  };

  const removeInstallment = (tempId: string) => {
    setFormData(prev => ({ ...prev, installments: prev.installments.filter(i => i._tempId !== tempId) }));
    setErrors(prev => withoutInstallmentErrors(prev));
  };

  // ── Generate installments ──────────────────────────────

  const formCompanyId = formData.company_id;
  useEffect(() => {
    if (!formCompanyId) {
      setCategoryGroups([]);
      return;
    }
    financialService
      .getAccountingPlans(formCompanyId)
      .then(plans => setCategoryGroups(buildCategoryOptions(plans)))
      .catch(() => setCategoryGroups([])); // sem categorias o seletor fica vazio; a provisão sai sem categoria
  }, [formCompanyId]);

  useEffect(() => {
    if (!formCompanyId) {
      setCreditCards([]);
      return;
    }
    purchaseOrderService
      .getPaymentCreditCards(formCompanyId)
      .then(setCreditCards)
      .catch(() => setCreditCards([]));
  }, [formCompanyId]);

  // Condição do fornecedor: no pedido novo, preenche o modelo de pagamento e a
  // quantidade de parcelas; "Gerar Parcelas" passa a usar os prazos dela.
  const formProviderId = formData.provider_id;
  useEffect(() => {
    if (!formProviderId) {
      setProviderTerm(null);
      return;
    }
    let cancelled = false;
    purchaseOrderService
      .getProviderPaymentTerm(formProviderId)
      .then(term => {
        if (cancelled) return;
        setProviderTerm(term);
        if (term && !isEditing) {
          setInstallmentCount(term.installments.length);
          setFormData(prev => ({ ...prev, payment_method_model_id: term.payment_method_model_id ?? prev.payment_method_model_id }));
        }
      })
      .catch(() => {
        if (!cancelled) setProviderTerm(null);
      });
    return () => {
      cancelled = true;
    };
  }, [formProviderId, isEditing]);

  const isCardMethod = useCallback(
    (paymentMethodId: number | undefined) =>
      isCreditCardMethodName(paymentMethods.find(m => m.payment_method_id === paymentMethodId)?.payment_method_name),
    [paymentMethods],
  );

  // Data do Pedido / Data Prevista: recalcula as parcelas com base (emissão /
  // entrega + dias) e avisa o que mudou num modal.
  const handleOrderDateChange = (field: 'order_date' | 'expected_date', value: string) => {
    const next = { ...formData, [field]: value };
    const { installments, changes } = recalcDueDates(formData.installments, next.order_date, next.expected_date);
    setFormData({ ...next, installments });
    if (changes.length > 0) {
      setDueDateChanges({ changes, fieldLabel: field === 'expected_date' ? 'Data Prevista' : 'Data do Pedido' });
      setErrors(prev => withoutInstallmentErrors(prev));
    }
  };

  const generateInstallments = () => {
    const orderTotal = totalProducts + (formData.freight_cost || 0);
    setInstallmentNotice(null);

    // Com condição do fornecedor: prazos, bases e % dela.
    if (providerTerm && providerTerm.installments.length > 0) {
      const r = buildInstallmentsFromTerm(providerTerm, orderTotal, formData.order_date, formData.expected_date);
      if (r.missingExpectedDate) {
        setInstallmentNotice('Informe a Data Prevista para calcular as parcelas que vencem após a entrega.');
        return;
      }
      if (r.missingOrderDate) {
        setInstallmentNotice('Informe a Data do Pedido para calcular as parcelas que vencem após a emissão.');
        return;
      }
      const cardId = isCardMethod(providerTerm.payment_method_id) ? defaultCardId(creditCards) : undefined;
      setFormData(prev => ({
        ...prev,
        installments: r.installments.map(inst => ({ ...inst, _tempId: uid(), credit_card_id: cardId ?? null })),
      }));
      setErrors(prev => withoutInstallmentErrors(prev));
      return;
    }

    // Sem condição: parcelas mensais a partir da Data do Pedido (ou de hoje).
    const count = typeof installmentCount === 'number' ? installmentCount : 0;
    if (count < 1 || count > 120) return;
    const newInstallments: PurchaseOrderInstallmentRow[] = buildMonthlyInstallments(
      count,
      orderTotal,
      formData.order_date || localTodayISO(),
      paymentMethods[0]?.payment_method_id,
    ).map(inst => ({ ...inst, _tempId: uid() }));
    setFormData(prev => ({ ...prev, installments: newInstallments }));
    setErrors(prev => withoutInstallmentErrors(prev));
  };

  // ── Calculated totals ─────────────────────────────────────────

  const totalProducts = formData.items.reduce((s, i) => s + (i.unit_value || 0) * (i.quantity || 0), 0);
  const totalInstallments = formData.installments.reduce((s, i) => s + (i.value || 0), 0);
  // Aviso (não bloqueia): parcelas que não somam o total do pedido.
  const installmentsMismatch = installmentsTotalMismatch(formData.installments, totalProducts + (formData.freight_cost || 0));

  // ── Validate ──────────────────────────────────────────────────

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!formData.company_id) e.company_id = 'Selecione uma empresa';
    if (!formData.provider_id) e.provider_id = 'Selecione um fornecedor';
    if (formData.items.length === 0) e.items = 'Adicione ao menos um item';
    // Parcela em branco não vai para a API: o backend a recusa (400) e a tela
    // ficava com um erro genérico.
    Object.assign(e, validateInstallments(formData.installments));
    Object.assign(e, cardInstallmentErrors(formData.installments, isCardMethod, creditCards.length > 0));
    setErrors(e);
    if (Object.keys(e).length > 0) setError('Alguns campos precisam ser corrigidos antes de salvar.');
    return Object.keys(e).length === 0;
  };

  // ── Save ──────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      setError(null);
      const saved = isEditing && id
        ? await purchaseOrderService.updatePurchaseOrder(parseInt(id), formData)
        : await purchaseOrderService.createPurchaseOrder(formData);
      // Parcela paga com cartão que não foi para a fatura: avisa antes de sair.
      if (saved?.warnings && saved.warnings.length > 0) {
        setSaveWarnings(saved.warnings);
        return;
      }
      navigate('/purchase-orders');
    } catch (err) {
      // Erro por campo da API aponta o campo (inclusive a linha da parcela);
      // o resto mostra a mensagem que o backend mandou.
      if (err instanceof ApiError && err.details) {
        setErrors(prev => ({ ...prev, ...detailsToFormErrors(err.details ?? {}) }));
      }
      setError(err instanceof Error ? err.message : 'Erro ao salvar pedido');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/purchase-orders')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        {[...Array(4)].map((_, i) => (
          <Card key={i}><CardContent><div className="h-20 bg-slate-200 rounded animate-pulse" /></CardContent></Card>
        ))}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/purchase-orders')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEditing ? 'Editar Pedido de Compra' : 'Novo Pedido de Compra'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isEditing ? `Editando pedido #${formData.order_number || id}` : 'Preencha os dados do pedido de compra'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      )}

      {/* ── 1. Dados Gerais ───────────────────────────────────── */}
      <Card>
        <CardHeader title="Dados Gerais" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Empresa */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Empresa <span className="text-red-500">*</span>
              </label>
              <select
                className={`input w-full ${errors.company_id ? 'border-red-400' : ''}`}
                value={formData.company_id || ''}
                onChange={e => updateField('company_id', e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">Selecione uma empresa...</option>
                {companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name} — CNPJ: {formatCnpj(c.document)}
                  </option>
                ))}
              </select>
              {errors.company_id && <p className="text-xs text-red-500 mt-1">{errors.company_id}</p>}
            </div>

            {/* Situação */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Situação</label>
              <select className="input w-full" value={formData.status} onChange={e => updateField('status', Number(e.target.value))}>
                {PURCHASE_ORDER_STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Fornecedor */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Fornecedor <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  className={`input flex-1 ${errors.provider_id ? 'border-red-400' : ''}`}
                  readOnly
                  placeholder="Selecione um fornecedor..."
                  value={providerLabel}
                />
                <Button variant="outline" onClick={() => setProviderModalOpen(true)} title="Buscar fornecedor">
                  <Search size={18} />
                </Button>
              </div>
              {errors.provider_id && <p className="text-xs text-red-500 mt-1">{errors.provider_id}</p>}
            </div>

            {/* Nº Pedido */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nº Pedido</label>
              <Input
                type="number"
                value={formData.order_number || ''}
                onChange={e => updateField('order_number', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="Automático"
              />
            </div>

            {/* Data do Pedido */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data do Pedido</label>
              <DateInputBR className="input w-full" value={formData.order_date || ''} onChange={v => handleOrderDateChange('order_date', v)} />
            </div>

            {/* Data Prevista */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data Prevista</label>
              <DateInputBR className="input w-full" value={formData.expected_date || ''} onChange={v => handleOrderDateChange('expected_date', v)} />
            </div>

            {/* Referência */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ordem de Compra (Referência)</label>
              <Input
                value={formData.purchase_order_reference || ''}
                onChange={e => updateField('purchase_order_reference', e.target.value)}
                placeholder="Ex: OC-2026-001"
              />
            </div>

            {/* Modelo Pagamento */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo de Pagamento</label>
              <select
                className="input w-full"
                value={formData.payment_method_model_id || ''}
                onChange={e => updateField('payment_method_model_id', e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">Selecione...</option>
                {paymentMethodModels.map(m => (
                  <option key={m.payment_method_model_id} value={m.payment_method_model_id}>
                    {m.payment_method_model_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Prioritário */}
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={formData.is_priority ?? false} onChange={e => updateField('is_priority', e.target.checked)} className="rounded border-slate-300" />
                <span className="inline-flex items-center gap-1">
                  <AlertTriangle size={14} className="text-red-500" /> Pedido prioritário
                </span>
              </label>
            </div>

            {/* Ativo */}
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={formData.is_active} onChange={e => updateField('is_active', e.target.checked)} className="rounded border-slate-300" />
                Pedido ativo
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Pedidos de Venda vinculados (N:N) ─────────────────── */}
      <Card>
        <CardHeader title="Pedidos de Venda vinculados" />
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">
            Vincule os pedidos de venda que dependem desta compra — o vínculo aparece na tela
            do pedido de compra, no recebimento de materiais e no calendário do dashboard.
          </p>

          {linkedSalesOrders.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {linkedSalesOrders.map(link => (
                <span key={link.order_id} className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1 text-sm">
                  PV #{link.order_id}
                  {link.customer_name ? ` · ${link.customer_name}` : ''}
                  {link.is_priority && <AlertTriangle size={12} className="text-red-500" />}
                  <button type="button" onClick={() => removeSalesOrderLink(link.order_id)} className="text-emerald-600 hover:text-red-600" title="Remover vínculo">
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={salesOrderQuery}
              onChange={e => setSalesOrderQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchSalesOrders(); } }}
              placeholder="Buscar por nº do pedido de venda ou nome do cliente"
              className="flex-1"
            />
            <Button type="button" variant="secondary" onClick={searchSalesOrders} disabled={salesOrderSearching}>
              {salesOrderSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            </Button>
          </div>

          {salesOrderResults.length > 0 && (
            <div className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
              {salesOrderResults.map(result => {
                const alreadyLinked = (formData.sales_order_ids ?? []).includes(result.order_id);
                return (
                  <button
                    key={result.order_id}
                    type="button"
                    disabled={alreadyLinked}
                    onClick={() => addSalesOrderLink(result)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${alreadyLinked ? 'text-slate-300 cursor-default' : 'hover:bg-slate-50 text-slate-700'}`}
                  >
                    <span>
                      PV #{result.order_id}
                      {result.customer_name ? ` · ${result.customer_name}` : ''}
                      {result.order_status ? ` · ${result.order_status}` : ''}
                    </span>
                    {alreadyLinked ? <span className="text-xs">vinculado</span> : <Plus size={14} />}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 2. Itens do Pedido ────────────────────────────────── */}
      <Card>
        <CardHeader title="Itens do Pedido" action={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" icon={<Package size={16} />} onClick={openProductModal}>
              Adicionar Produto
            </Button>
            <Button variant="outline" size="sm" icon={<Hammer size={16} />} onClick={() => setRawMaterialModalOpen(true)}>
              Adicionar Matéria-Prima
            </Button>
          </div>
        } />
        <CardContent>
          {!formData.provider_id && (
            <div className="flex items-center gap-2 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <AlertCircle size={16} className="shrink-0" />
              Selecione um fornecedor antes de adicionar produtos. A busca de produtos retornará o preço de custo específico do fornecedor selecionado.
            </div>
          )}
          {errors.items && <p className="text-xs text-red-500 mb-3">{errors.items}</p>}
          {formData.items.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              Nenhum item adicionado. Use os botões acima para adicionar produtos ou matérias-primas.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="py-2 px-2 w-20">Tipo</th>
                    <th className="py-2 px-2 w-24">SKU</th>
                    <th className="py-2 px-2 w-24" title="Código do fornecedor">
                      Cód. Fornecedor
                    </th>
                    <th className="py-2 px-2">Descrição</th>
                    <th className="py-2 px-2 w-24">Unidade</th>
                    <th className="py-2 px-2 w-28 text-right">Valor Unit.</th>
                    <th className="py-2 px-2 w-24 text-right">Qtde</th>
                    <th className="py-2 px-2 w-20 text-right">IPI %</th>
                    <th className="py-2 px-2 w-28 text-right">Subtotal</th>
                    <th className="py-2 px-2 w-12" />
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map(item => (
                    <tr key={item._tempId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${item._type === 'product' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {item._type === 'product' ? <Package size={12} /> : <Hammer size={12} />}
                          {item._type === 'product' ? 'Prod' : 'MP'}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-xs text-slate-600 font-mono">
                          {item._sourceCode || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <span className="text-xs text-slate-600">
                          {item.supplier_code || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <input
                          className="input w-full text-xs"
                          value={item.description}
                          onChange={e => updateItem(item._tempId, 'description', e.target.value)}
                        />
                      </td>
                      <td className="py-2 px-2">
                        <select
                          className="input w-full text-xs"
                          value={item.unit || 'UN'}
                          onChange={e => updateItem(item._tempId, 'unit', e.target.value)}
                        >
                          {UNIT_OPTIONS.map(u => (
                            <option key={u.value} value={u.value}>{u.value}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <input
                          className="input w-full text-xs text-right"
                          type="number"
                          step="0.01"
                          min={0}
                          value={item.unit_value}
                          onChange={e => updateItem(item._tempId, 'unit_value', parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td className="py-2 px-2 text-right">
                        <input
                          className="input w-full text-xs text-right"
                          type="number"
                          step="0.01"
                          min={0}
                          value={item.quantity}
                          onChange={e => updateItem(item._tempId, 'quantity', parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td className="py-2 px-2 text-right">
                        <input
                          className="input w-full text-xs text-right"
                          type="number"
                          step="0.01"
                          min={0}
                          value={item.ipi_rate || ''}
                          onChange={e => updateItem(item._tempId, 'ipi_rate', e.target.value ? parseFloat(e.target.value) : undefined)}
                        />
                      </td>
                      <td className="py-2 px-2 text-right font-medium text-slate-900 whitespace-nowrap">
                        {formatCurrency((item.unit_value || 0) * (item.quantity || 0))}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <button
                          className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                          onClick={() => removeItem(item._tempId)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300">
                    <td colSpan={7} className="py-2 px-2 text-right font-semibold text-slate-700">Total Produtos:</td>
                    <td className="py-2 px-2 text-right font-bold text-emerald-700">{formatCurrency(totalProducts)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 3. Transporte ─────────────────────────────────────── */}
      <Card>
        <CardHeader title="Transporte" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Transportadora</label>
              <Input value={formData.carrier || ''} onChange={e => updateField('carrier', e.target.value)} placeholder="Nome da transportadora" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Frete por conta</label>
              <select className="input w-full" value={formData.freight_responsibility ?? ''} onChange={e => updateField('freight_responsibility', e.target.value !== '' ? Number(e.target.value) : undefined)}>
                <option value="">Selecione...</option>
                {FREIGHT_RESPONSIBILITY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Valor do Frete (R$)</label>
              <Input type="number" step="0.01" min={0} value={formData.freight_cost ?? ''} onChange={e => updateField('freight_cost', e.target.value ? parseFloat(e.target.value) : undefined)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Peso Bruto (kg)</label>
              <Input type="number" step="0.01" min={0} value={formData.gross_weight ?? ''} onChange={e => updateField('gross_weight', e.target.value ? parseFloat(e.target.value) : undefined)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Volumes</label>
              <Input type="number" min={0} value={formData.volumes ?? ''} onChange={e => updateField('volumes', e.target.value ? parseInt(e.target.value) : undefined)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── 4. Desconto & Tributação ──────────────────────────── */}
      <Card>
        <CardHeader title="Desconto e Tributação" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Desconto</label>
              <div className="flex gap-2">
                <Input className="flex-1" type="number" step="0.01" min={0} value={formData.discount_value ?? ''} onChange={e => updateField('discount_value', e.target.value ? parseFloat(e.target.value) : undefined)} />
                <select className="input w-32" value={formData.discount_unit || 'REAL'} onChange={e => updateField('discount_unit', e.target.value)}>
                  {DISCOUNT_UNIT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Total ICMS (R$)</label>
              <Input type="number" step="0.01" min={0} value={formData.total_icms ?? ''} onChange={e => updateField('total_icms', e.target.value ? parseFloat(e.target.value) : undefined)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Total IPI (R$)</label>
              <Input type="number" step="0.01" min={0} value={formData.total_ipi ?? ''} onChange={e => updateField('total_ipi', e.target.value ? parseFloat(e.target.value) : undefined)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── 5. Parcelas ───────────────────────────────────────── */}
      <Card>
        <CardHeader title="Parcelas" action={
          <Button variant="outline" size="sm" icon={<Plus size={16} />} onClick={addInstallment}>
            Adicionar Parcela
          </Button>
        } />
        <CardContent>
          {/* Gerar parcelas automaticamente */}
          <div className="flex items-end gap-3 mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Qtde de Parcelas</label>
              <input
                type="number"
                min={1}
                max={120}
                className="input w-28 text-sm"
                placeholder="Ex: 4"
                value={installmentCount}
                onChange={e => setInstallmentCount(e.target.value ? parseInt(e.target.value) : '')}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              icon={<Calculator size={16} />}
              onClick={generateInstallments}
              disabled={!providerTerm && (!installmentCount || installmentCount < 1)}
              title={providerTerm ? 'Gera as parcelas pela condição de pagamento do fornecedor' : 'Gera parcelas mensais a partir da Data do Pedido'}
            >
              Gerar Parcelas
            </Button>
            <span className="text-xs text-slate-500 pb-2">
              Total para distribuição: {formatCurrency(totalProducts + (formData.freight_cost || 0))}
            </span>
            <div className="ml-auto">
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Categoria financeira das provisões
              </label>
              <CategorySelect
                groups={categoryGroups}
                value={formData.accounting_plan_id ?? null}
                onChange={erpId => updateField('accounting_plan_id', erpId ?? undefined)}
                disabled={!formData.company_id}
                emptyLabel="— Sem categoria (classificar depois) —"
              />
            </div>
          </div>
          {providerTerm && (
            <div className="flex items-start gap-2 mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              <CalendarClock size={14} className="shrink-0 mt-0.5" />
              <span>
                Condição do fornecedor:{' '}
                <strong>
                  {paymentMethods.find(m => m.payment_method_id === providerTerm.payment_method_id)?.payment_method_name ?? providerTerm.payment_method_name}
                  {' · '}
                  {describeTerm(providerTerm.installments)}
                </strong>
                . "Gerar Parcelas" usa esses prazos; as parcelas após a entrega acompanham a Data Prevista.
              </span>
            </div>
          )}
          {installmentNotice && (
            <p className="flex items-center gap-1 mb-2 text-xs text-amber-700">
              <AlertTriangle size={12} /> {installmentNotice}
            </p>
          )}
          <p className="text-xs text-slate-500 mt-2">
            As parcelas geram lançamentos provisionados na conta corrente da empresa, na categoria
            escolhida — sem categoria, eles aparecem em "Despesas não mapeadas" no relatório. Parcela paga
            com cartão de crédito vai para a fatura do cartão escolhido.
          </p>
          {formData.installments.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              Nenhuma parcela adicionada. Clique em "Adicionar Parcela" para incluir.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="py-2 px-2 w-10">#</th>
                    <th className="py-2 px-2 w-36">Vencimento</th>
                    <th className="py-2 px-2 w-32 text-right">Valor (R$)</th>
                    <th className="py-2 px-2">Forma de Pagamento</th>
                    <th className="py-2 px-2">Observação</th>
                    <th className="py-2 px-2 w-12" />
                  </tr>
                </thead>
                <tbody>
                  {formData.installments.map((inst, idx) => (
                    <tr key={inst._tempId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-2 text-slate-500 font-medium">{idx + 1}</td>
                      <td className="py-2 px-2 align-top">
                        <DateInputBR
                          className={`input w-full text-xs ${errors[installmentErrorKey(idx, 'due_date')] ? 'border-red-400' : ''}`}
                          value={inst.due_date || ''}
                          onChange={v => updateInstallment(inst._tempId, 'due_date', v)}
                        />
                        {errors[installmentErrorKey(idx, 'due_date')] && (
                          <p className="text-[11px] text-red-500 mt-1">{errors[installmentErrorKey(idx, 'due_date')]}</p>
                        )}
                        {(inst.due_date_base != null || providerTerm) && (
                          <p className="text-[10px] text-slate-400 mt-0.5" title="Base do vencimento (condição do fornecedor)">
                            {dueBaseLabel(inst.due_date_base, inst.due_days)}
                          </p>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right align-top">
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          className={`input w-full text-xs text-right ${errors[installmentErrorKey(idx, 'value')] ? 'border-red-400' : ''}`}
                          value={inst.value}
                          onChange={e => updateInstallment(inst._tempId, 'value', parseFloat(e.target.value) || 0)}
                        />
                        {errors[installmentErrorKey(idx, 'value')] && (
                          <p className="text-[11px] text-red-500 mt-1 text-left">{errors[installmentErrorKey(idx, 'value')]}</p>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <select
                          className="input w-full text-xs"
                          value={inst.payment_method_id || ''}
                          onChange={e => updateInstallment(inst._tempId, 'payment_method_id', e.target.value ? Number(e.target.value) : undefined)}
                        >
                          <option value="">Selecione...</option>
                          {paymentMethods.map(m => (
                            <option key={m.payment_method_id} value={m.payment_method_id}>{m.payment_method_name}</option>
                          ))}
                        </select>
                        {isCardMethod(inst.payment_method_id) && creditCards.length > 0 && (
                          <>
                            <select
                              className={`input w-full text-xs mt-1 ${errors[`installments.${idx}.credit_card_id`] ? 'border-red-400' : ''}`}
                              value={inst.credit_card_id || ''}
                              onChange={e => updateInstallment(inst._tempId, 'credit_card_id', e.target.value ? Number(e.target.value) : null)}
                              title="Cartão da empresa — a provisão vai para a fatura dele"
                            >
                              <option value="">Cartão…</option>
                              {creditCards.map(c => (
                                <option key={c.credit_card_id} value={c.credit_card_id}>{c.description}</option>
                              ))}
                            </select>
                            {errors[`installments.${idx}.credit_card_id`] && (
                              <p className="text-[11px] text-red-500 mt-1">{errors[`installments.${idx}.credit_card_id`]}</p>
                            )}
                          </>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <input
                          className="input w-full text-xs"
                          value={inst.notes || ''}
                          onChange={e => updateInstallment(inst._tempId, 'notes', e.target.value)}
                          placeholder="Observação da parcela..."
                        />
                      </td>
                      <td className="py-2 px-2 text-center">
                        <button className="p-1 text-slate-400 hover:text-red-500 transition-colors" onClick={() => removeInstallment(inst._tempId)}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300">
                    <td />
                    <td className="py-2 px-2 text-right font-semibold text-slate-700">Total Parcelas:</td>
                    <td className="py-2 px-2 text-right font-bold text-emerald-700">{formatCurrency(totalInstallments)}</td>
                    <td colSpan={3} className="py-2 px-2 text-xs text-amber-700">
                      {installmentsMismatch != null && (
                        <span className="inline-flex items-center gap-1">
                          <AlertTriangle size={12} />
                          {installmentsMismatch > 0 ? 'Parcelas somam ' : 'Parcelas ficam '}
                          {formatCurrency(Math.abs(installmentsMismatch))}
                          {installmentsMismatch > 0 ? ' acima' : ' abaixo'} do total do pedido
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 6. Observações ────────────────────────────────────── */}
      <Card>
        <CardHeader title="Observações" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Observações</label>
              <textarea
                className="input w-full min-h-[80px] resize-y"
                placeholder="Observações do pedido..."
                value={formData.notes || ''}
                onChange={e => updateField('notes', e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Observações Internas</label>
              <textarea
                className="input w-full min-h-[80px] resize-y"
                placeholder="Uso interno, não será visível no pedido..."
                value={formData.internal_notes || ''}
                onChange={e => updateField('internal_notes', e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Actions ───────────────────────────────────────────── */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/purchase-orders')} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving} icon={<Save size={20} />}>
          {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Pedido'}
        </Button>
      </div>

      {/* ── Search Modals ─────────────────────────────────────── */}

      {/* Provider */}
      <SearchModal<Provider>
        isOpen={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        title="Buscar Fornecedor"
        onSelect={handleProviderSelect}
        searchFn={searchProviders}
        keyFn={p => p.provider_id}
        renderItem={p => (
          <div>
            <span className="font-medium text-slate-900">{p.trade_name || p.legal_name}</span>
            <span className="block text-xs text-slate-500">{p.document} • {p.email || '—'}</span>
          </div>
        )}
      />

      {/* Product (by provider) */}
      <SearchModal<ProviderProduct>
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        title={`Buscar Produto — ${providerLabel || 'Fornecedor'}`}
        onSelect={handleProductSelect}
        searchFn={searchProducts}
        keyFn={p => p.product_id}
        renderItem={p => (
          <div className="flex items-center gap-3">
            <Package size={16} className="text-blue-500 shrink-0" />
            <div>
              <span className="font-medium text-slate-900">{p.description}</span>
              <span className="block text-xs text-slate-500">
                SKU: {p.sku}{p.supplier_code ? ` • Cód. Forn: ${p.supplier_code}` : ''} • Custo Fornecedor: {formatCurrency(p.provider_cost)} • Preço Venda: {formatCurrency(p.price)}
              </span>
            </div>
          </div>
        )}
      />

      {/* Raw Material */}
      <SearchModal<RawMaterial>
        isOpen={rawMaterialModalOpen}
        onClose={() => setRawMaterialModalOpen(false)}
        title="Buscar Matéria-Prima"
        onSelect={handleRawMaterialSelect}
        searchFn={searchRawMaterials}
        keyFn={r => r.id}
        renderItem={r => (
          <div className="flex items-center gap-3">
            <Hammer size={16} className="text-amber-500 shrink-0" />
            <div>
              <span className="font-medium text-slate-900">{r.description}</span>
              <span className="block text-xs text-slate-500">
                Código: {r.code} • Custo: {formatCurrency(r.cost)} • {r.unit || '—'}
              </span>
            </div>
          </div>
        )}
      />

      <DueDatesUpdatedModal
        changes={dueDateChanges?.changes ?? null}
        fieldLabel={dueDateChanges?.fieldLabel ?? 'Data Prevista'}
        onClose={() => setDueDateChanges(null)}
      />
      <SaveWarningsModal
        warnings={saveWarnings}
        onClose={() => {
          setSaveWarnings(null);
          navigate('/purchase-orders');
        }}
      />
    </div>
  );
};
